<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-spectrum/layout';
import {HeaderInfo, PropTable, FunctionAPI} from '@react-spectrum/docs';
import packageData from '@react-spectrum/layout/package.json';

```jsx import
import {Grid, repeat, minmax} from '@react-spectrum/layout';
import {View} from '@react-spectrum/view';
```

---
category: Layout
---

# Grid

<p>{docs.exports.Grid.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['Grid']} />

## Introduction

The `Grid` component can be used to layout its children in two dimensions with
[CSS grid](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Grid_Layout/Basic_Concepts_of_Grid_Layout).
Any React Spectrum component can be used as a child, and Spectrum [dimension variables](styling.html#dimension-values)
can be used to define sizing and spacing, which ensures consistency across applications, and allows the layout to adapt
to different devices automatically. In addition, these values can be autocompleted in many IDEs for convenience.

## Examples

### Explicit grids

This example shows how to build a common application layout with a header, sidebar, content area, and footer.
It uses the `areas` prop to define the grid areas, along with `columns` and `rows` to define the sizes.
Each child uses the `gridArea` prop to declare which area it should be placed in.

```tsx example
<Grid
  areas={[
    'header  header',
    'sidebar content',
    'footer  footer'
  ]}
  columns={['size-3000', 'auto']}
  rows={['size-1000', 'auto', 'size-1000']}
  height="size-6000"
  gap="size-100">
  <View backgroundColor="celery-600" gridArea="header" />
  <View backgroundColor="blue-600" gridArea="sidebar" />
  <View backgroundColor="purple-600" gridArea="content" />
  <View backgroundColor="magenta-600" gridArea="footer" />
</Grid>
```

### Implicit grids

This example creates an implicit grid. It declares the columns using the `repeat` function to automatically generate
as many columns as fit, and uses the `autoRows` prop to set the size of the implicitly created rows. The items are
centered horizontally within the container, and have a gap between them defined using a Spectrum dimension variable.
Resize your browser to see how items reflow.

```tsx import
let baseColors = ['celery', 'chartreuse', 'yellow', 'magenta', 'fuchsia', 'purple', 'indigo', 'seafoam', 'red', 'orange', 'green', 'blue'];
let colors = [];
for (let color of baseColors) {
  for (let i = 4; i <= 7; i++) {
    colors.push(`${color}-${i}00`);
  }
}
```

```tsx example
<Grid
  columns={repeat('auto-fit', 'size-800')}
  autoRows="size-800"
  justifyContent="center"
  gap="size-100">
  {colors.map(color =>
    <View key={color} backgroundColor={color} />
  )}
</Grid>
```

## Props

<PropTable component={docs.exports.Grid} links={docs.links} />

## Helper functions

React Spectrum offers JavaScript versions of the CSS functions that are often used with CSS grid.
These allow using Spectrum variables to define dimensions, and are typed to allow IDE autocomplete.

### repeat

The [repeat](https://developer.mozilla.org/en-US/docs/Web/CSS/repeat) function can be used to make
a repeating fragment of the columns or rows list.

<FunctionAPI function={docs.exports.repeat} links={docs.links} />

### minmax

The [minmax](https://developer.mozilla.org/en-US/docs/Web/CSS/minmax) function defines a size range
greater than or equal to `min` and less than or equal to `max`.

<FunctionAPI function={docs.exports.minmax} links={docs.links} />

### fitContent

The [fitContent](https://developer.mozilla.org/en-US/docs/Web/CSS/fit-content) function clamps a given
size to an available size.

<FunctionAPI function={docs.exports.fitContent} links={docs.links} />
